/**
 * FloorPlanSketch — touch-based 2D floor plan canvas for FieldProIQ.
 *
 * Single component. SVG canvas. Coordinates are in GRID UNITS (1 unit =
 * one grid square; one grid square represents N feet per plan.scale).
 *
 * Tools: wall, door, window, label, dimension, eraser, move, moisture-pin,
 * equipment, affected-area, photo-pin.
 *
 * State that needs to round-trip with the backend:
 *   - plan: { scale, active_level_id, levels: [{id,name,index,rooms:[{id,points,label,doors,windows}]}], grid_visible, angle_snap }
 *   - pins[], equipment[], affected_areas[], photos[]
 *
 * Coordinate transform:  screenX = (gridX - panX) * zoom + canvasOriginX
 */
import {
  useState, useEffect, useRef, useCallback, useMemo,
} from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Layout, Pencil, Square, DoorOpen, RectangleHorizontal, Type as TypeIcon,
  Ruler, Eraser, Move, MapPin, Wrench, Layers as LayersIcon, Camera,
  Undo2, Redo2, Grid3x3, ZoomIn, ZoomOut, Save, Download, X, Plus,
  Loader2, CheckCircle2, AlertTriangle, Crosshair, Trash2,
  TrendingUp, TrendingDown, Minus, ChevronRight, RotateCw, FlipHorizontal, FlipVertical, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { parseDimension, formatFeetInches } from "@/lib/dimensions";
import { PHASE_A_CATALOG, findCatalogItem } from "@/components/floorplan_catalog";

const TOOLS = [
  { id: "wall",      icon: Pencil,              label: "Wall" },
  { id: "door",      icon: DoorOpen,            label: "Door" },
  { id: "window",    icon: RectangleHorizontal, label: "Window" },
  { id: "label",     icon: TypeIcon,            label: "Label" },
  { id: "dim",       icon: Ruler,               label: "Dim" },
  { id: "move",      icon: Move,                label: "Move" },
  { id: "pin",       icon: MapPin,              label: "Pin" },
  { id: "equipment", icon: Wrench,              label: "Equip" },
  { id: "affected",  icon: Square,              label: "Area" },
  { id: "photo",     icon: Camera,              label: "Photo" },
  { id: "eraser",    icon: Eraser,              label: "Erase" },
];

const MATERIALS = ["Drywall", "Hardwood Floor", "Subfloor", "Carpet",
                   "Concrete", "Tile", "Brick", "Wood Stud", "Insulation", "Other"];
const METERS    = ["Pin Meter", "Non-Penetrating/Pinless Meter", "Thermo-Hygrometer"];
const HEIGHTS   = ["Floor Level (0-2 ft)", "Mid-Wall (2-4 ft)",
                   "Upper Wall (4-6 ft)", "Ceiling"];
const CONDITIONS = [
  { v: "dry",       label: "Dry",       color: "#16a34a" },
  { v: "elevated",  label: "Elevated",  color: "#eab308" },
  { v: "wet",       label: "Wet",       color: "#f97316" },
  { v: "saturated", label: "Saturated", color: "#dc2626" },
];
const READING_TYPES = [
  { v: "moisture_content", label: "Moisture %" },
  { v: "wme",              label: "WME" },
  { v: "rh",               label: "RH %" },
];
const EQUIPMENT_TYPES = [
  "Dehumidifier (LGR)", "Dehumidifier (Desiccant)", "Dehumidifier (Conventional)",
  "Air Mover", "Air Scrubber / HEPA", "Injection Drying System",
  "Injectidry Panel", "Negative Air Machine", "Hydroxyl Generator",
  "Heater / Heating System", "Thermal Imaging Camera",
];
const DAMAGE_TYPES = [
  { v: "water",       label: "Water Damage",      color: "#3b82f6" },
  { v: "fire",        label: "Fire Damage",       color: "#f97316" },
  { v: "mold",        label: "Mold",              color: "#22c55e" },
  { v: "smoke",       label: "Smoke / Soot",      color: "#6b7280" },
  { v: "structural",  label: "Structural Damage", color: "#dc2626" },
  { v: "custom",      label: "Custom",            color: "#a855f7" },
];
const LEVEL_NAMES = ["Ground Floor", "Level 2", "Level 3", "Basement", "Attic"];

// ── Geometry helpers ────────────────────────────────────────────────────
const ANGLE_SNAP_DEGREES = 45;
function snapAngle(prev, curr) {
  if (!prev) return curr;
  const dx = curr.x - prev.x, dy = curr.y - prev.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return curr;
  const theta = Math.atan2(dy, dx);
  const step = (Math.PI / 180) * ANGLE_SNAP_DEGREES;
  const snapped = Math.round(theta / step) * step;
  return { x: prev.x + Math.cos(snapped) * len, y: prev.y + Math.sin(snapped) * len };
}
function snapGrid(p) {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}
function polygonArea(pts) {
  const n = pts.length;
  if (n < 3) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}
function polygonCentroid(pts) {
  if (pts.length === 0) return { x: 0, y: 0 };
  const sum = pts.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / pts.length, y: sum.y / pts.length };
}
function ftLabel(gridLen, scale) {
  const mul = { "1ft": 1, "2ft": 2, "4ft": 4 }[scale] || 1;
  return `${(gridLen * mul).toFixed(1)} ft`;
}
function sqftLabel(gridArea, scale) {
  const mul = { "1ft": 1, "2ft": 2, "4ft": 4 }[scale] || 1;
  return `${Math.round(gridArea * mul * mul)} sq ft`;
}

// ── Main component ──────────────────────────────────────────────────────
export default function FloorPlanSketch({ requestId, jobInfo, onClose }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Drawing state
  const [tool, setTool] = useState("wall");
  const [pendingPoints, setPendingPoints] = useState([]);   // wall/affected area in progress
  const [pendingAffectedPolygon, setPendingAffectedPolygon] = useState([]);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);   // {kind, id}
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [showDims, setShowDims] = useState(true);
  const [angleSnap, setAngleSnap] = useState(true);
  const [history, setHistory] = useState([]);     // undo stack
  const [redoStack, setRedoStack] = useState([]); // redo stack

  // Modal/sheet state
  const [pinSheet, setPinSheet] = useState(null);          // {x,y} or existing pin
  const [equipmentSheet, setEquipmentSheet] = useState(null);
  const [affectedDialog, setAffectedDialog] = useState(null);
  const [labelDialog, setLabelDialog] = useState(null);    // {roomId}
  const [pinDetail, setPinDetail] = useState(null);        // existing pin to view/edit
  const [exporting, setExporting] = useState(false);

  // Wall feature placement (Phase A)
  const [features, setFeatures] = useState([]);
  const [wallMenu, setWallMenu] = useState(null);   // { roomId, segIdx, gx, gy, screenX, screenY }
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureSheet, setFeatureSheet] = useState(null); // feature for label/note edit
  const [wallDimEdit, setWallDimEdit] = useState(null);   // { roomId, segIdx, value }

  // ── Load ──
  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/floor-plans/${requestId}`);
      if (!data.plan) {
        // Empty initial state
        const lid = crypto.randomUUID();
        data.plan = {
          scale: "1ft",
          active_level_id: lid,
          grid_visible: true,
          angle_snap: true,
          levels: [{ id: lid, name: "Ground Floor", index: 0, rooms: [] }],
        };
      }
      setBundle(data);
      setShowGrid(data.plan.grid_visible !== false);
      setAngleSnap(data.plan.angle_snap !== false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load floor plan");
    } finally { setLoading(false); }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  // ── Undo snapshot ──
  const snapshot = useCallback(() => {
    setHistory(h => [...h.slice(-30), JSON.stringify(bundle)]);
    setRedoStack([]);
  }, [bundle]);
  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setRedoStack(r => [...r, JSON.stringify(bundle)]);
    setHistory(h => h.slice(0, -1));
    setBundle(JSON.parse(last));
  };
  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setHistory(h => [...h, JSON.stringify(bundle)]);
    setRedoStack(r => r.slice(0, -1));
    setBundle(JSON.parse(next));
  };

  // ── Save (auto + manual) ──
  const savePlan = useCallback(async (silent = false) => {
    if (!bundle?.plan) return;
    setSaving(true);
    try {
      const body = {
        scale: bundle.plan.scale,
        active_level_id: bundle.plan.active_level_id,
        levels: bundle.plan.levels,
        grid_visible: showGrid,
        angle_snap: angleSnap,
      };
      const { data } = await API.put(`/floor-plans/${requestId}`, body);
      setBundle(b => ({ ...b, plan: data.plan }));
      setLastSavedAt(new Date());
      if (!silent) toast.success("Floor plan saved");
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Save failed");
      // offline fallback — store in localStorage
      try {
        localStorage.setItem(`floorplan_offline_${requestId}`,
                             JSON.stringify(bundle));
      } catch {}
    } finally { setSaving(false); }
  }, [bundle, requestId, showGrid, angleSnap]);

  useEffect(() => {
    const i = setInterval(() => savePlan(true), 30000);
    return () => clearInterval(i);
  }, [savePlan]);

  // Save on tool switch
  useEffect(() => {
    if (lastSavedAt) savePlan(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  // ── Reload bundle helpers (used after creating pin/equipment/etc) ──
  const reloadBundle = async () => {
    const { data } = await API.get(`/floor-plans/${requestId}`);
    if (data.plan) setBundle(data);
  };

  // ── Features (Phase A) ──
  const loadFeatures = useCallback(async () => {
    try {
      const { data } = await API.get(`/floor-plans/${requestId}/features`);
      setFeatures(data.features || []);
    } catch { /* non-fatal */ }
  }, [requestId]);
  useEffect(() => { loadFeatures(); }, [loadFeatures]);

  // ── Coordinate transforms (SVG viewBox + zoom/pan) ──
  const VIEWPORT_W = 800, VIEWPORT_H = 1000;
  const CELL = 20; // pixels per grid unit at zoom=1
  const viewBox = useMemo(() => {
    const w = VIEWPORT_W / zoom, h = VIEWPORT_H / zoom;
    return `${pan.x * CELL} ${pan.y * CELL} ${w} ${h}`;
  }, [pan, zoom]);

  const svgRef = useRef(null);
  const screenToGrid = useCallback((sx, sy) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const vw = VIEWPORT_W / zoom, vh = VIEWPORT_H / zoom;
    const gx = (sx - rect.left) / rect.width * vw / CELL + pan.x;
    const gy = (sy - rect.top) / rect.height * vh / CELL + pan.y;
    return { x: gx, y: gy };
  }, [pan, zoom]);

  // ── Pointer / touch handlers ──
  const pointerCacheRef = useRef(new Map());
  const lastPinchDistRef = useRef(null);
  const lastPanRef = useRef(null);

  const onPointerDown = e => {
    pointerCacheRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointerCacheRef.current.size >= 2) {
      const pts = Array.from(pointerCacheRef.current.values());
      lastPinchDistRef.current = Math.hypot(
        pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      lastPanRef.current = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      };
    }
  };
  const onPointerMove = e => {
    if (!pointerCacheRef.current.has(e.pointerId)) {
      const g = screenToGrid(e.clientX, e.clientY);
      setHoverPoint(angleSnap && pendingPoints.length
        ? snapGrid(snapAngle(pendingPoints[pendingPoints.length - 1], g))
        : snapGrid(g));
      return;
    }
    pointerCacheRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointerCacheRef.current.size >= 2) {
      const pts = Array.from(pointerCacheRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      if (lastPinchDistRef.current) {
        const ratio = dist / lastPinchDistRef.current;
        setZoom(z => Math.max(0.25, Math.min(4, z * ratio)));
      }
      if (lastPanRef.current) {
        const dx = (mid.x - lastPanRef.current.x) / CELL / zoom;
        const dy = (mid.y - lastPanRef.current.y) / CELL / zoom;
        setPan(p => ({ x: p.x - dx, y: p.y - dy }));
      }
      lastPinchDistRef.current = dist;
      lastPanRef.current = mid;
    }
  };
  const onPointerUp = e => {
    pointerCacheRef.current.delete(e.pointerId);
    if (pointerCacheRef.current.size < 2) {
      lastPinchDistRef.current = null;
      lastPanRef.current = null;
    }
  };

  // ── Tap on canvas — dispatch to active tool ──
  const handleCanvasTap = e => {
    if (pointerCacheRef.current.size > 0) return;
    const g = snapGrid(screenToGrid(e.clientX, e.clientY));

    // ── Direct placement: when "door" or "window" tool is active, tapping a wall
    //    places a default door/window immediately (no menu).
    if ((tool === "door" || tool === "window") && pendingPoints.length === 0) {
      const hit = detectWallTap(g.x, g.y);
      if (hit) {
        const defaultKey = tool === "door" ? "door_swing_in" : "window_fixed";
        // Fire-and-forget; emulate wallMenu so placeFeature works
        setWallMenu({ ...hit, screenX: e.clientX, screenY: e.clientY });
        setTimeout(() => placeFeature(defaultKey), 0);
        return;
      }
      toast.message(`Tap on a wall to place a ${tool}. Draw walls first if none exist.`);
      return;
    }

    // ── Phase A: wall-tap context menu (works with any tool except active wall drawing) ──
    if (tool !== "wall" && pendingPoints.length === 0 && pendingAffectedPolygon.length === 0) {
      const hit = detectWallTap(g.x, g.y);
      if (hit) {
        setWallMenu({ ...hit, screenX: e.clientX, screenY: e.clientY });
        return;
      } else if (wallMenu) {
        // Tap on empty canvas dismisses the menu
        setWallMenu(null);
      }
    }
    if (selectedFeature && tool !== "eraser") setSelectedFeature(null);

    if (tool === "wall") {
      const last = pendingPoints[pendingPoints.length - 1];
      const point = angleSnap && last ? snapGrid(snapAngle(last, g)) : g;
      // Close-room detection: tap near first point with >=3 points already
      if (pendingPoints.length >= 3) {
        const first = pendingPoints[0];
        if (Math.hypot(first.x - point.x, first.y - point.y) < 1.5) {
          // close the room
          closeRoom();
          return;
        }
      }
      snapshot();
      setPendingPoints([...pendingPoints, point]);
    } else if (tool === "pin") {
      setPinSheet({ x: g.x, y: g.y });
    } else if (tool === "equipment") {
      setEquipmentSheet({ x: g.x, y: g.y });
    } else if (tool === "affected") {
      const last = pendingAffectedPolygon[pendingAffectedPolygon.length - 1];
      if (pendingAffectedPolygon.length >= 3) {
        const first = pendingAffectedPolygon[0];
        if (Math.hypot(first.x - g.x, first.y - g.y) < 1.5) {
          setAffectedDialog({ polygon: pendingAffectedPolygon });
          return;
        }
      }
      const pt = angleSnap && last ? snapGrid(snapAngle(last, g)) : g;
      setPendingAffectedPolygon([...pendingAffectedPolygon, pt]);
    } else if (tool === "photo") {
      handleAddPhotoPin(g);
    } else if (tool === "eraser") {
      // tap is handled per-element via onClick
    }
  };

  const closeRoom = () => {
    if (pendingPoints.length < 3 || !activeLevel) return;
    const room = {
      id: crypto.randomUUID(),
      points: pendingPoints.map(p => [p.x, p.y]),
      label: "",
      doors: [], windows: [], dimensions_visible: [],
    };
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: b.plan.levels.map(l => l.id === b.plan.active_level_id
          ? { ...l, rooms: [...l.rooms, room] } : l),
      },
    }));
    setPendingPoints([]);
    setTimeout(() => setLabelDialog({ roomId: room.id }), 50);
  };

  const cancelPending = () => {
    setPendingPoints([]);
    setPendingAffectedPolygon([]);
  };

  // ── Active level helpers ──
  const activeLevel = bundle?.plan?.levels?.find(
    l => l.id === bundle.plan.active_level_id);

  const switchLevel = (id) => {
    setBundle(b => ({ ...b, plan: { ...b.plan, active_level_id: id } }));
    cancelPending();
  };
  const addLevel = () => {
    const nextName = LEVEL_NAMES[bundle.plan.levels.length] || `Level ${bundle.plan.levels.length + 1}`;
    const id = crypto.randomUUID();
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: [...b.plan.levels,
                 { id, name: nextName, index: b.plan.levels.length, rooms: [] }],
        active_level_id: id,
      },
    }));
  };

  // ── Delete handlers (eraser tool) ──
  const eraseRoom = roomId => {
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: b.plan.levels.map(l => l.id === b.plan.active_level_id
          ? { ...l, rooms: l.rooms.filter(r => r.id !== roomId) } : l),
      },
    }));
  };
  const erasePin = async pinId => {
    try {
      await API.delete(`/floor-plans/${requestId}/pins/${pinId}`);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };
  const eraseEquipment = async id => {
    try {
      await API.delete(`/floor-plans/${requestId}/equipment/${id}`);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };
  const eraseAffectedArea = async id => {
    try {
      await API.delete(`/floor-plans/${requestId}/affected-areas/${id}`);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };
  const erasePhotoPin = async id => {
    try {
      await API.delete(`/floor-plans/${requestId}/photos/${id}`);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };

  // ── Submit handlers ──
  const submitPin = async (form) => {
    try {
      await API.post(`/floor-plans/${requestId}/pins`, {
        level_id: bundle.plan.active_level_id,
        x: pinSheet.x, y: pinSheet.y,
        material_type: form.material_type,
        meter_type: form.meter_type,
        height_on_wall: form.height_on_wall || null,
        reading: {
          value: parseFloat(form.value) || 0,
          type: form.type,
          condition: form.condition,
          notes: form.notes,
          photo: form.photo,
        },
      });
      toast.success("Pin placed");
      setPinSheet(null);
      reloadBundle();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const addReadingToPin = async (pinId, form) => {
    try {
      await API.patch(`/floor-plans/${requestId}/pins/${pinId}/reading`, {
        value: parseFloat(form.value) || 0,
        type: form.type, condition: form.condition,
        notes: form.notes, photo: form.photo,
      });
      toast.success("Reading added");
      setPinDetail(null);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };

  const submitEquipment = async (form) => {
    try {
      await API.post(`/floor-plans/${requestId}/equipment`, {
        level_id: bundle.plan.active_level_id,
        x: equipmentSheet.x, y: equipmentSheet.y,
        equipment_type: form.equipment_type,
        asset_id: form.asset_id, make_model: form.make_model,
        date_placed: form.date_placed || new Date().toISOString(),
      });
      toast.success("Equipment placed");
      setEquipmentSheet(null);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };

  const submitAffectedArea = async (damage, label) => {
    try {
      const dt = DAMAGE_TYPES.find(d => d.v === damage) || DAMAGE_TYPES[0];
      await API.post(`/floor-plans/${requestId}/affected-areas`, {
        level_id: bundle.plan.active_level_id,
        polygon_points: affectedDialog.polygon.map(p => [p.x, p.y]),
        damage_type: damage, color: dt.color, opacity: 0.3, label,
      });
      toast.success("Affected area saved");
      setAffectedDialog(null);
      setPendingAffectedPolygon([]);
      reloadBundle();
    } catch (e) { toast.error("Failed"); }
  };

  const handleAddPhotoPin = async (g) => {
    // Use file input to capture photo
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*";
    inp.capture = "environment";
    inp.onchange = async (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await API.post(`/floor-plans/${requestId}/photos`, {
            level_id: bundle.plan.active_level_id,
            x: g.x, y: g.y,
            photos: [reader.result],
          });
          toast.success("Photo added");
          reloadBundle();
        } catch (e) { toast.error("Failed"); }
      };
      reader.readAsDataURL(file);
    };
    inp.click();
  };

  const setRoomLabel = (roomId, text) => {
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: b.plan.levels.map(l => l.id === b.plan.active_level_id
          ? { ...l, rooms: l.rooms.map(r => r.id === roomId
              ? { ...r, label: text } : r) }
          : l),
      },
    }));
    setLabelDialog(null);
    setTimeout(() => savePlan(true), 100);
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      // Use fetch + Blob to force a download in headless browsers and Safari/iOS.
      const baseURL = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("apex_token") || "";
      const res = await fetch(`${baseURL}/api/floor-plans/${requestId}/export/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `floor-plan-${requestId}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  // ── Wall-tap detection (Phase A) ──
  // Returns { roomId, segIdx, gx, gy } if the tap is within 0.6 grid units
  // of any wall segment in the active level. Else null.
  const detectWallTap = useCallback((gx, gy) => {
    const lvl = bundle?.plan?.levels?.find(l => l.id === bundle?.plan?.active_level_id);
    if (!lvl) return null;
    let best = null;
    for (const room of lvl.rooms) {
      const pts = room.points;
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i];
        const [bx, by] = pts[(i + 1) % pts.length];
        // distance from (gx,gy) to segment (a,b)
        const dx = bx - ax, dy = by - ay;
        const t = Math.max(0, Math.min(1, ((gx - ax) * dx + (gy - ay) * dy) / (dx*dx + dy*dy || 1)));
        const px = ax + t * dx, py = ay + t * dy;
        const d = Math.hypot(gx - px, gy - py);
        if (d < 0.6 && (!best || d < best.d)) {
          best = { roomId: room.id, segIdx: i, gx: px, gy: py, d };
        }
      }
    }
    return best;
  }, [bundle]);

  // Place a feature on the wall (or freestanding)
  const placeFeature = async (catalogKey) => {
    if (!wallMenu) return;
    const item = findCatalogItem(catalogKey);
    if (!item) return;
    try {
      const lvl = bundle.plan.levels.find(l => l.id === bundle.plan.active_level_id);
      const room = lvl.rooms.find(r => r.id === wallMenu.roomId);
      const wallSegId = `${room.id}:${wallMenu.segIdx}`;
      // Rotation: align to wall angle
      const a = room.points[wallMenu.segIdx];
      const b = room.points[(wallMenu.segIdx + 1) % room.points.length];
      const angle = Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI;

      const payload = {
        level_id: bundle.plan.active_level_id,
        wall_segment_id: item.wall ? wallSegId : null,
        room_id: room.id,
        x: wallMenu.gx, y: wallMenu.gy,
        rotation: item.wall ? angle : 0,
        width_ft: item.w, depth_ft: item.d,
        flip_h: false, flip_v: false,
        category: item.category, type_key: item.key,
      };

      try {
        await API.post(`/floor-plans/${requestId}/features`, payload);
      } catch (err) {
        // If the plan doesn't exist yet on the backend, save it first then retry
        const detail = err?.response?.data?.detail || "";
        if (err?.response?.status === 400 && detail.toLowerCase().includes("floor plan")) {
          await savePlan(true);
          await API.post(`/floor-plans/${requestId}/features`, payload);
        } else {
          throw err;
        }
      }

      toast.success(`${item.label} placed`);
      setWallMenu(null);
      loadFeatures();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to place";
      toast.error(msg);
      console.error("placeFeature failed:", e);
    }
  };

  const patchFeature = async (id, patch) => {
    setFeatures(fs => fs.map(f => f.id === id ? { ...f, ...patch } : f));
    try { await API.patch(`/floor-plans/${requestId}/features/${id}`, patch); }
    catch { loadFeatures(); }
  };
  const removeFeature = async (id) => {
    setSelectedFeature(null);
    try { await API.delete(`/floor-plans/${requestId}/features/${id}`); loadFeatures(); }
    catch { toast.error("Delete failed"); }
  };
  const duplicateFeature = async (f) => {
    try {
      await API.post(`/floor-plans/${requestId}/features`, {
        level_id: f.level_id, wall_segment_id: null, room_id: f.room_id,
        x: f.x + 1, y: f.y + 1, rotation: f.rotation,
        width_ft: f.width_ft, depth_ft: f.depth_ft,
        flip_h: f.flip_h, flip_v: f.flip_v,
        category: f.category, type_key: f.type_key,
        label: f.label, note: f.note, damage_flagged: f.damage_flagged,
      });
      loadFeatures();
    } catch { toast.error("Duplicate failed"); }
  };

  // Apply parsed wall length to a segment
  const applyWallLength = async () => {
    if (!wallDimEdit) return;
    const parsed = parseDimension(wallDimEdit.value);
    if (!parsed.ok) { toast.error("That measurement doesn't fit. Adjust connected walls first."); return; }
    const { roomId, segIdx } = wallDimEdit;
    const lvl = bundle.plan.levels.find(l => l.id === bundle.plan.active_level_id);
    const room = lvl.rooms.find(r => r.id === roomId);
    if (!room) return;
    const a = room.points[segIdx], b = room.points[(segIdx + 1) % room.points.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const curr = Math.hypot(dx, dy);
    if (curr < 0.01) return;
    const scaleFt = { "1ft": 1, "2ft": 2, "4ft": 4 }[bundle.plan.scale] || 1;
    const targetGrid = parsed.feet / scaleFt;
    const ratio = targetGrid / curr;
    const newB = [a[0] + dx * ratio, a[1] + dy * ratio];
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: b.plan.levels.map(l => l.id !== bundle.plan.active_level_id ? l : {
          ...l, rooms: l.rooms.map(r => r.id !== roomId ? r : {
            ...r,
            points: r.points.map((p, i) =>
              i === (segIdx + 1) % r.points.length ? newB : p),
          }),
        }),
      },
    }));
    setWallDimEdit(null);
    setTimeout(() => savePlan(true), 100);
    toast.success(`Wall set to ${parsed.label}`);
  };

  // Split a wall segment at the menu tap point
  const splitWall = () => {
    if (!wallMenu) return;
    snapshot();
    setBundle(b => ({
      ...b,
      plan: {
        ...b.plan,
        levels: b.plan.levels.map(l => l.id !== b.plan.active_level_id ? l : {
          ...l, rooms: l.rooms.map(r => r.id !== wallMenu.roomId ? r : {
            ...r,
            points: [
              ...r.points.slice(0, wallMenu.segIdx + 1),
              [wallMenu.gx, wallMenu.gy],
              ...r.points.slice(wallMenu.segIdx + 1),
            ],
          }),
        }),
      },
    }));
    setWallMenu(null);
    setTimeout(() => savePlan(true), 100);
    toast.success("Wall segment split");
  };

  // ── RENDER ──
  if (loading) return (
    <div className="fixed inset-0 z-[100] bg-zinc-900/70 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-white" />
    </div>
  );

  if (!bundle?.plan) return null;
  const scale = bundle.plan.scale;
  const levels = bundle.plan.levels || [];
  const currentLevelPins = (bundle.pins || []).filter(
    p => p.level_id === bundle.plan.active_level_id);
  const currentLevelEquipment = (bundle.equipment || []).filter(
    e => e.level_id === bundle.plan.active_level_id);
  const currentLevelAreas = (bundle.affected_areas || []).filter(
    a => a.level_id === bundle.plan.active_level_id);
  const currentLevelPhotos = (bundle.photos || []).filter(
    p => p.level_id === bundle.plan.active_level_id);

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col" data-testid="floor-plan-sketch">
      {/* ── Top Bar ── */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-zinc-900 text-white border-b border-zinc-800">
        <Button onClick={onClose} size="sm" variant="ghost"
                className="text-white hover:bg-white/10" data-testid="fp-close">
          <X className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-[140px]">
          <p className="text-xs font-bold truncate">Floor Plan · {jobInfo?.code || requestId.slice(0, 8)}</p>
          <p className="text-[10px] text-zinc-400">Scale: 1 sq = {scale} · {lastSavedAt
            ? `Saved ${lastSavedAt.toLocaleTimeString()}`
            : bundle.plan.updated_at ? `Saved ${new Date(bundle.plan.updated_at).toLocaleTimeString()}` : "Unsaved"}</p>
        </div>
        {/* Scale selector */}
        <Select value={scale}
                onValueChange={v => { snapshot(); setBundle(b => ({ ...b,
                  plan: { ...b.plan, scale: v } })); }}>
          <SelectTrigger className="w-24 h-8 text-xs bg-zinc-800 border-zinc-700"
                         data-testid="fp-scale">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1ft">1 sq = 1 ft</SelectItem>
            <SelectItem value="2ft">1 sq = 2 ft</SelectItem>
            <SelectItem value="4ft">1 sq = 4 ft</SelectItem>
          </SelectContent>
        </Select>
        {/* Level switcher */}
        <Select value={bundle.plan.active_level_id} onValueChange={switchLevel}>
          <SelectTrigger className="w-32 h-8 text-xs bg-zinc-800 border-zinc-700"
                         data-testid="fp-level">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={addLevel} size="sm" variant="ghost"
                className="text-white hover:bg-white/10" title="Add level"
                data-testid="fp-add-level">
          <Plus className="w-4 h-4" />
        </Button>
        <Button onClick={undo} size="sm" variant="ghost" disabled={!history.length}
                className="text-white hover:bg-white/10" data-testid="fp-undo">
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button onClick={redo} size="sm" variant="ghost" disabled={!redoStack.length}
                className="text-white hover:bg-white/10" data-testid="fp-redo">
          <Redo2 className="w-4 h-4" />
        </Button>
        <Button onClick={() => setShowGrid(g => !g)} size="sm" variant="ghost"
                className={`text-white hover:bg-white/10 ${showGrid ? "bg-white/20" : ""}`}
                data-testid="fp-grid">
          <Grid3x3 className="w-4 h-4" />
        </Button>
        <Button onClick={() => setAngleSnap(s => !s)} size="sm" variant="ghost"
                className={`text-white text-xs hover:bg-white/10 ${angleSnap ? "bg-white/20" : ""}`}
                data-testid="fp-anglesnap">
          90°
        </Button>
        <Button onClick={() => setShowDims(d => !d)} size="sm" variant="ghost"
                className={`text-white hover:bg-white/10 ${showDims ? "bg-white/20" : ""}`}
                data-testid="fp-dims">
          <Ruler className="w-4 h-4" />
        </Button>
        <Button onClick={() => {
                  if (!selectedFeature) {
                    toast.message("Tap a placed element first, then press Delete Selected");
                    return;
                  }
                  removeFeature(selectedFeature.id);
                  toast.success("Element deleted");
                }}
                size="sm" variant="ghost"
                disabled={!selectedFeature}
                className={`text-white hover:bg-white/10 disabled:opacity-40 ${selectedFeature ? "bg-red-600/40" : ""}`}
                title="Delete the currently selected element"
                data-testid="fp-delete-selected">
          <Trash2 className="w-4 h-4" />
        </Button>
        <Button onClick={() => savePlan()} size="sm" disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                data-testid="fp-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
        </Button>
        <Button onClick={exportPDF} size="sm" disabled={exporting}
                variant="outline"
                className="h-8 text-xs bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                data-testid="fp-export">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                     : <><Download className="w-3.5 h-3.5 mr-1" /> PDF</>}
        </Button>
      </div>

      {/* ── Canvas ── */}
      <div className="flex-1 overflow-hidden bg-white relative touch-none">
        <svg ref={svgRef} viewBox={viewBox}
             onPointerDown={onPointerDown}
             onPointerMove={onPointerMove}
             onPointerUp={onPointerUp}
             onPointerCancel={onPointerUp}
             onClick={handleCanvasTap}
             style={{ width: "100%", height: "100%", display: "block",
                      background: "#fff", touchAction: "none" }}
             data-testid="fp-canvas">
          {/* Grid */}
          {showGrid && (
            <defs>
              <pattern id="grid" width={CELL} height={CELL} patternUnits="userSpaceOnUse">
                <path d={`M ${CELL} 0 L 0 0 0 ${CELL}`}
                      fill="none" stroke="#e4e4e7" strokeWidth="0.5" />
              </pattern>
            </defs>
          )}
          {showGrid && <rect x={pan.x * CELL} y={pan.y * CELL}
                              width={VIEWPORT_W / zoom} height={VIEWPORT_H / zoom}
                              fill="url(#grid)" />}

          {/* Other levels dimmed */}
          {levels.filter(l => l.id !== bundle.plan.active_level_id).map(l => (
            <g key={l.id} opacity="0.3">
              {l.rooms.map(r => (
                <polygon key={r.id}
                  points={r.points.map(p => `${p[0]*CELL},${p[1]*CELL}`).join(" ")}
                  fill="none" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4,4" />
              ))}
            </g>
          ))}

          {/* Affected area polygons */}
          {currentLevelAreas.map(a => (
            <g key={a.id} onClick={(e) => {
                e.stopPropagation();
                if (tool === "eraser") eraseAffectedArea(a.id);
              }}>
              <polygon
                points={a.polygon_points.map(p => `${p[0]*CELL},${p[1]*CELL}`).join(" ")}
                fill={a.color} fillOpacity={a.opacity}
                stroke={a.color} strokeWidth="1.5"
                style={{ cursor: tool === "eraser" ? "pointer" : "default" }} />
              {a.label && (() => {
                const c = polygonCentroid(a.polygon_points.map(p => ({ x: p[0], y: p[1] })));
                return (
                  <text x={c.x * CELL} y={c.y * CELL} textAnchor="middle"
                        fill="#111" fontSize="10" fontWeight="600"
                        style={{ pointerEvents: "none" }}>
                    {a.label} · {a.sq_ft} sq ft
                  </text>
                );
              })()}
            </g>
          ))}

          {/* Rooms */}
          {activeLevel?.rooms.map(r => {
            const pts = r.points.map(p => ({ x: p[0], y: p[1] }));
            const c = polygonCentroid(pts);
            const area = polygonArea(pts);
            return (
              <g key={r.id}>
                <polygon
                  points={r.points.map(p => `${p[0]*CELL},${p[1]*CELL}`).join(" ")}
                  fill="white" stroke="#111" strokeWidth="2.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tool === "eraser") eraseRoom(r.id);
                    else if (tool === "label") setLabelDialog({ roomId: r.id });
                  }}
                  style={{ cursor: ["eraser", "label"].includes(tool) ? "pointer" : "default" }} />
                {/* Dimensions */}
                {showDims && r.points.map((p, i) => {
                  const next = r.points[(i + 1) % r.points.length];
                  const mx = (p[0] + next[0]) / 2, my = (p[1] + next[1]) / 2;
                  const len = Math.hypot(next[0] - p[0], next[1] - p[1]);
                  if (len < 0.5) return null;
                  return (
                    <text key={i} x={mx * CELL} y={my * CELL - 4}
                          fill="#71717a" fontSize="9" textAnchor="middle"
                          style={{ pointerEvents: "none" }}>
                      {ftLabel(len, scale)}
                    </text>
                  );
                })}
                {/* Label */}
                <text x={c.x * CELL} y={c.y * CELL} textAnchor="middle"
                      fill="#111" fontSize="12" fontWeight="700"
                      style={{ pointerEvents: "none" }}>
                  {r.label || "Untitled"}
                </text>
                <text x={c.x * CELL} y={c.y * CELL + 14} textAnchor="middle"
                      fill="#52525b" fontSize="10"
                      style={{ pointerEvents: "none" }}>
                  {sqftLabel(area, scale)}
                </text>
              </g>
            );
          })}

          {/* Pending wall points */}
          {pendingPoints.length > 0 && (
            <g>
              {pendingPoints.map((p, i) => {
                const next = pendingPoints[i + 1];
                return next ? (
                  <line key={i} x1={p.x * CELL} y1={p.y * CELL}
                                x2={next.x * CELL} y2={next.y * CELL}
                                stroke="#dc2626" strokeWidth="2" />
                ) : null;
              })}
              {hoverPoint && pendingPoints.length > 0 && (
                <>
                  <line x1={pendingPoints[pendingPoints.length - 1].x * CELL}
                        y1={pendingPoints[pendingPoints.length - 1].y * CELL}
                        x2={hoverPoint.x * CELL} y2={hoverPoint.y * CELL}
                        stroke="#dc2626" strokeWidth="1.5" strokeDasharray="3,3" />
                  <text x={hoverPoint.x * CELL + 6} y={hoverPoint.y * CELL - 6}
                        fill="#dc2626" fontSize="10" fontWeight="600">
                    {ftLabel(Math.hypot(
                      hoverPoint.x - pendingPoints[pendingPoints.length - 1].x,
                      hoverPoint.y - pendingPoints[pendingPoints.length - 1].y),
                      scale)}
                  </text>
                </>
              )}
              {pendingPoints.map((p, i) => (
                <circle key={`v${i}`} cx={p.x * CELL} cy={p.y * CELL} r="4"
                        fill={i === 0 ? "#16a34a" : "#dc2626"} />
              ))}
            </g>
          )}

          {/* Pending affected polygon */}
          {pendingAffectedPolygon.length > 0 && (
            <g opacity="0.7">
              <polyline points={pendingAffectedPolygon.map(p => `${p.x * CELL},${p.y * CELL}`).join(" ")}
                        fill="none" stroke="#3b82f6" strokeWidth="2" />
              {pendingAffectedPolygon.map((p, i) => (
                <circle key={i} cx={p.x * CELL} cy={p.y * CELL} r="4"
                        fill={i === 0 ? "#16a34a" : "#3b82f6"} />
              ))}
            </g>
          )}

          {/* Moisture pins */}
          {currentLevelPins.map(p => {
            const cond = CONDITIONS.find(c => c.v === p.current_condition) || CONDITIONS[0];
            return (
              <g key={p.id} onClick={(e) => {
                  e.stopPropagation();
                  if (tool === "eraser") erasePin(p.id);
                  else setPinDetail(p);
                }}
                 style={{ cursor: "pointer" }} data-testid={`fp-pin-${p.id}`}>
                <circle cx={p.x * CELL} cy={p.y * CELL} r="9"
                        fill={cond.color} stroke="white" strokeWidth="2" />
                <text x={p.x * CELL} y={p.y * CELL + 3}
                      textAnchor="middle" fill="white" fontSize="9" fontWeight="700">
                  {Math.round(p.current_value)}
                </text>
                {p.stale_count >= 3 && (
                  <circle cx={p.x * CELL + 8} cy={p.y * CELL - 8} r="4"
                          fill="#dc2626" stroke="white" strokeWidth="1.5" />
                )}
              </g>
            );
          })}

          {/* Equipment icons */}
          {currentLevelEquipment.map(e => (
            <g key={e.id} onClick={(ev) => {
                ev.stopPropagation();
                if (tool === "eraser") eraseEquipment(e.id);
              }}
               opacity={e.active ? 1 : 0.4}
               style={{ cursor: tool === "eraser" ? "pointer" : "default" }}
               data-testid={`fp-eq-${e.id}`}>
              <rect x={e.x * CELL - 8} y={e.y * CELL - 8} width="16" height="16"
                    fill="#0ea5e9" stroke="white" strokeWidth="1.5" rx="3" />
              <text x={e.x * CELL} y={e.y * CELL + 3} textAnchor="middle"
                    fill="white" fontSize="9" fontWeight="700">
                {e.equipment_type.slice(0, 1)}
              </text>
              {e.asset_id && (
                <text x={e.x * CELL} y={e.y * CELL + 18} textAnchor="middle"
                      fill="#0c4a6e" fontSize="8">
                  {e.asset_id}
                </text>
              )}
              {!e.active && (
                <text x={e.x * CELL + 10} y={e.y * CELL - 6}
                      fill="#16a34a" fontSize="12" fontWeight="700">✓</text>
              )}
            </g>
          ))}

          {/* Photo pins */}
          {currentLevelPhotos.map(pp => (
            <g key={pp.id} onClick={(e) => {
                e.stopPropagation();
                if (tool === "eraser") erasePhotoPin(pp.id);
                else window.open(pp.photos?.[0], "_blank");
              }}
              style={{ cursor: "pointer" }} data-testid={`fp-photo-${pp.id}`}>
              <circle cx={pp.x * CELL} cy={pp.y * CELL} r="8"
                      fill="#a855f7" stroke="white" strokeWidth="1.5" />
              <text x={pp.x * CELL} y={pp.y * CELL + 3} textAnchor="middle"
                    fill="white" fontSize="10">📷</text>
            </g>
          ))}

          {/* ── Wall Features (Phase A) ── */}
          {features.filter(f => f.level_id === bundle.plan.active_level_id).map(f => {
            const item = findCatalogItem(f.type_key);
            if (!item) return null;
            const px = f.x * CELL, py = f.y * CELL;
            const w = (f.width_ft || item.w) * CELL / ({"1ft":1,"2ft":2,"4ft":4}[bundle.plan.scale] || 1);
            const h = (f.depth_ft || item.d) * CELL / ({"1ft":1,"2ft":2,"4ft":4}[bundle.plan.scale] || 1);
            const isSel = selectedFeature?.id === f.id;
            const flipScale = `scale(${f.flip_h ? -1 : 1},${f.flip_v ? -1 : 1})`;
            return (
              <g key={f.id} transform={`translate(${px} ${py}) rotate(${f.rotation || 0}) ${flipScale}`}
                 onClick={(e) => { e.stopPropagation();
                                   if (tool === "eraser") removeFeature(f.id);
                                   else setSelectedFeature(f); }}
                 style={{ cursor: "pointer" }}
                 data-testid={`fp-feature-${f.id}`}>
                {item.render(Math.max(w, 12), Math.max(h, 12))}
                {f.damage_flagged && (
                  <circle cx={w / 2 + 4} cy={-h / 2 - 4} r="4" fill="#dc2626" stroke="white" />
                )}
                {f.label && (
                  <text x={0} y={h / 2 + 9} textAnchor="middle"
                        fontSize="7" fill="#52525b">{f.label}</text>
                )}
                {isSel && (
                  <rect x={-w / 2 - 4} y={-h / 2 - 4}
                        width={w + 8} height={h + 8}
                        fill="none" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="3,2" />
                )}
              </g>
            );
          })}

          {/* ── Wall dimension labels (tappable for editing) ── */}
          {showDims && activeLevel?.rooms.flatMap(r =>
            r.points.map((p, i) => {
              const next = r.points[(i + 1) % r.points.length];
              const mx = (p[0] + next[0]) / 2;
              const my = (p[1] + next[1]) / 2;
              const len = Math.hypot(next[0] - p[0], next[1] - p[1]);
              if (len < 1) return null;
              return (
                <g key={`${r.id}-dim-edit-${i}`}
                   onClick={(e) => { e.stopPropagation();
                                     const scale = {"1ft":1,"2ft":2,"4ft":4}[bundle.plan.scale]||1;
                                     setWallDimEdit({ roomId: r.id, segIdx: i,
                                                     value: formatFeetInches(len * scale) }); }}
                   style={{ cursor: "pointer" }}>
                  <rect x={mx * CELL - 12} y={my * CELL - 14}
                        width={24} height={10} fill="white" stroke="#e4e4e7"
                        rx="2" opacity={0.001} />
                </g>
              );
            })
          )}
        </svg>

        {/* Zoom controls */}
        <div className="absolute right-3 bottom-20 flex flex-col gap-1">
          <Button onClick={() => setZoom(z => Math.min(4, z * 1.25))} size="sm"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white w-10 h-10"
                  data-testid="fp-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
          <div className="text-[10px] text-center font-mono text-zinc-700 py-0.5 bg-white/70 rounded">
            {Math.round(zoom * 100)}%
          </div>
          <Button onClick={() => setZoom(z => Math.max(0.25, z / 1.25))} size="sm"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white w-10 h-10"
                  data-testid="fp-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
        </div>

        {/* Cancel pending */}
        {(pendingPoints.length > 0 || pendingAffectedPolygon.length > 0) && (
          <Button onClick={cancelPending} size="sm"
                  className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="fp-cancel-pending">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
      </div>

      {/* ── Bottom Toolbar ── */}
      <div className="flex items-center justify-around bg-zinc-900 border-t border-zinc-800 px-1 py-1.5 overflow-x-auto">
        {TOOLS.map(t => {
          const Active = t.icon;
          const isActive = tool === t.id;
          return (
            <Button key={t.id}
                    onClick={() => { setTool(t.id); cancelPending(); setSelectedItem(null); }}
                    variant="ghost"
                    className={`flex flex-col items-center justify-center min-w-[48px] h-12 px-1 rounded ${
                      isActive ? "bg-red-600 text-white" : "text-zinc-300 hover:bg-zinc-800"
                    }`}
                    data-testid={`fp-tool-${t.id}`}>
              <Active className="w-4 h-4" />
              <span className="text-[9px] mt-0.5">{t.label}</span>
            </Button>
          );
        })}
      </div>

      {/* ── Pin data entry sheet ── */}
      {pinSheet && (
        <PinSheet pinSheet={pinSheet} onSubmit={submitPin}
                  onClose={() => setPinSheet(null)} />
      )}
      {pinDetail && (
        <PinDetailSheet pin={pinDetail}
                        onAddReading={(form) => addReadingToPin(pinDetail.id, form)}
                        onClose={() => setPinDetail(null)}
                        scale={scale} />
      )}
      {equipmentSheet && (
        <EquipmentSheet equipmentSheet={equipmentSheet}
                        onSubmit={submitEquipment}
                        onClose={() => setEquipmentSheet(null)} />
      )}
      {affectedDialog && (
        <AffectedAreaDialog onSubmit={submitAffectedArea}
                            onClose={() => { setAffectedDialog(null); setPendingAffectedPolygon([]); }} />
      )}
      {labelDialog && (
        <LabelDialog roomId={labelDialog.roomId}
                     current={activeLevel?.rooms.find(r => r.id === labelDialog.roomId)?.label || ""}
                     onSubmit={(text) => setRoomLabel(labelDialog.roomId, text)}
                     onClose={() => setLabelDialog(null)} />
      )}

      {/* ── Wall context menu (Phase A) ── */}
      {wallMenu && (
        <div className="fixed z-[110] bg-white rounded-lg shadow-2xl border border-zinc-200 w-72 max-h-[60vh] overflow-y-auto"
             style={{ left: Math.min(wallMenu.screenX, window.innerWidth - 300),
                      top:  Math.min(wallMenu.screenY, window.innerHeight - 420) }}
             data-testid="wall-context-menu">
          <div className="flex items-center justify-between px-3 py-2 bg-zinc-900 text-white">
            <p className="text-xs font-bold">Wall action</p>
            <button onClick={() => setWallMenu(null)} data-testid="wall-menu-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-2 space-y-1 border-b border-zinc-100">
            <p className="text-[10px] font-bold uppercase text-zinc-500 px-1">Wall Actions</p>
            <button onClick={() => { setWallDimEdit({
                roomId: wallMenu.roomId, segIdx: wallMenu.segIdx,
                value: ""
              }); setWallMenu(null); }}
                    className="block w-full text-left text-xs px-2 py-1.5 hover:bg-zinc-50 rounded"
                    data-testid="wall-menu-editdim">
              <Ruler className="w-3.5 h-3.5 inline mr-1" /> Edit Wall Dimensions
            </button>
            <button onClick={() => { eraseRoom(wallMenu.roomId); setWallMenu(null); }}
                    className="block w-full text-left text-xs px-2 py-1.5 hover:bg-red-50 text-red-600 rounded"
                    data-testid="wall-menu-delete">
              <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Delete Wall Segment (full room)
            </button>
            <button onClick={splitWall}
                    className="block w-full text-left text-xs px-2 py-1.5 hover:bg-zinc-50 rounded"
                    data-testid="wall-menu-split">
              <Plus className="w-3.5 h-3.5 inline mr-1" /> Split Wall Here
            </button>
          </div>
          <div className="p-2">
            <p className="text-[10px] font-bold uppercase text-zinc-500 px-1 mb-1">Add Feature Here</p>
            {PHASE_A_CATALOG.map(cat => (
              <CategorySection key={cat.category} category={cat}
                               onPick={(k) => placeFeature(k)} />
            ))}
          </div>
        </div>
      )}

      {/* ── Wall dimension inline edit ── */}
      {wallDimEdit && (
        <Dialog open onOpenChange={() => setWallDimEdit(null)}>
          <DialogContent className="max-w-xs" data-testid="wall-dim-edit">
            <DialogHeader>
              <DialogTitle>Wall Length</DialogTitle>
              <DialogDescription className="text-[10px]">
                Accepts <code>6'6"</code>, <code>6.5</code>, <code>78</code> (in.), <code>2.5m</code>, etc.
              </DialogDescription>
            </DialogHeader>
            <Input value={wallDimEdit.value} autoFocus
                   onChange={e => setWallDimEdit(w => ({ ...w, value: e.target.value }))}
                   onKeyDown={e => e.key === "Enter" && applyWallLength()}
                   placeholder={"12'6\""}
                   data-testid="wall-dim-input" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setWallDimEdit(null)} className="flex-1">Cancel</Button>
              <Button onClick={applyWallLength}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="wall-dim-apply">Apply</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Selected feature toolbar (Phase A) ── */}
      {selectedFeature && (
        <div className="fixed z-[105] bg-zinc-900 text-white rounded-full shadow-lg flex items-center gap-1 px-2 py-1.5"
             style={{ left: "50%", bottom: 80, transform: "translateX(-50%)" }}
             data-testid="feature-toolbar">
          <button onClick={() => patchFeature(selectedFeature.id,
                                              { flip_h: !selectedFeature.flip_h,
                                                flip_v: selectedFeature.flip_v })}
                  className="p-2 hover:bg-white/10 rounded-full" title="Flip H"
                  data-testid="feat-flip-h">
            <FlipHorizontal className="w-4 h-4" />
          </button>
          <button onClick={() => patchFeature(selectedFeature.id,
                                              { flip_v: !selectedFeature.flip_v,
                                                flip_h: selectedFeature.flip_h })}
                  className="p-2 hover:bg-white/10 rounded-full" title="Flip V"
                  data-testid="feat-flip-v">
            <FlipVertical className="w-4 h-4" />
          </button>
          <button onClick={() => patchFeature(selectedFeature.id,
                                              { rotation: ((selectedFeature.rotation || 0) + 45) % 360 })}
                  className="p-2 hover:bg-white/10 rounded-full" title="Rotate 45°"
                  data-testid="feat-rotate">
            <RotateCw className="w-4 h-4" />
          </button>
          <button onClick={() => duplicateFeature(selectedFeature)}
                  className="p-2 hover:bg-white/10 rounded-full" title="Duplicate"
                  data-testid="feat-duplicate">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={() => setFeatureSheet(selectedFeature)}
                  className="p-2 hover:bg-white/10 rounded-full" title="Label/Note"
                  data-testid="feat-labelnote">
            <TypeIcon className="w-4 h-4" />
          </button>
          <button onClick={() => removeFeature(selectedFeature.id)}
                  className="p-2 hover:bg-red-500/40 rounded-full" title="Delete"
                  data-testid="feat-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Feature label/note sheet ── */}
      {featureSheet && (
        <Dialog open onOpenChange={() => setFeatureSheet(null)}>
          <DialogContent className="max-w-md" data-testid="feature-label-sheet">
            <DialogHeader>
              <DialogTitle>{findCatalogItem(featureSheet.type_key)?.label || "Feature"}</DialogTitle>
              <DialogDescription className="text-xs">
                Label appears on the canvas. Note appears in the report.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-xs">
              <Input value={featureSheet.label || ""} placeholder="Short label"
                     onChange={e => setFeatureSheet(f => ({ ...f, label: e.target.value }))}
                     data-testid="feat-label-input" />
              <Textarea rows={3} value={featureSheet.note || ""} placeholder="Notes"
                        onChange={e => setFeatureSheet(f => ({ ...f, note: e.target.value }))}
                        data-testid="feat-note-input" />
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={featureSheet.damage_flagged || false}
                       onChange={e => setFeatureSheet(f => ({ ...f, damage_flagged: e.target.checked }))}
                       className="accent-red-600" data-testid="feat-damage-flag" />
                This element is damaged / involved in the loss
              </label>
              <Button onClick={async () => {
                  await patchFeature(featureSheet.id, {
                    label: featureSheet.label || "",
                    note: featureSheet.note || "",
                    damage_flagged: !!featureSheet.damage_flagged,
                  });
                  setFeatureSheet(null);
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="feat-label-save">
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Inline category accordion for the wall-tap context menu
function CategorySection({ category, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-100">
      <button onClick={() => setOpen(o => !o)}
              className="flex items-center justify-between w-full px-2 py-1.5 hover:bg-zinc-50 rounded"
              data-testid={`wm-cat-${category.category}`}>
        <span className="text-xs font-semibold text-zinc-900">{category.category}</span>
        <span className="text-[10px] text-zinc-400">{category.items.length}</span>
      </button>
      {open && (
        <div className="pl-2">
          {category.items.map(it => (
            <button key={it.key} onClick={() => onPick(it.key)}
                    className="block w-full text-left text-[11px] px-2 py-1 hover:bg-red-50 rounded text-zinc-700"
                    data-testid={`wm-item-${it.key}`}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────
function PinSheet({ pinSheet, onSubmit, onClose }) {
  const [form, setForm] = useState({
    material_type: "Drywall", meter_type: "Pin Meter",
    height_on_wall: "Floor Level (0-2 ft)",
    type: "moisture_content", condition: "elevated",
    value: "", notes: "", photo: null,
  });
  const needsHeight = ["Drywall", "Brick"].includes(form.material_type);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"
                     data-testid="fp-pin-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crosshair className="w-5 h-5 text-red-600" /> Add Moisture Pin
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Log a moisture reading at the tapped location.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-zinc-700">Material</label>
            <Select value={form.material_type}
                    onValueChange={v => setForm(f => ({ ...f, material_type: v }))}>
              <SelectTrigger data-testid="pin-material"><SelectValue /></SelectTrigger>
              <SelectContent>{MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-zinc-700">Reading value *</label>
              <Input type="number" value={form.value} required
                     onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                     placeholder="18.5" data-testid="pin-value" />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-700">Type</label>
              <Select value={form.type}
                      onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="pin-type"><SelectValue /></SelectTrigger>
                <SelectContent>{READING_TYPES.map(r =>
                  <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-700">Meter</label>
            <Select value={form.meter_type}
                    onValueChange={v => setForm(f => ({ ...f, meter_type: v }))}>
              <SelectTrigger data-testid="pin-meter"><SelectValue /></SelectTrigger>
              <SelectContent>{METERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsHeight && (
            <div>
              <label className="text-xs font-semibold text-zinc-700">Height on wall</label>
              <Select value={form.height_on_wall}
                      onValueChange={v => setForm(f => ({ ...f, height_on_wall: v }))}>
                <SelectTrigger data-testid="pin-height"><SelectValue /></SelectTrigger>
                <SelectContent>{HEIGHTS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-zinc-700">Condition</label>
            <div className="grid grid-cols-4 gap-1">
              {CONDITIONS.map(c => (
                <button key={c.v}
                        onClick={() => setForm(f => ({ ...f, condition: c.v }))}
                        className={`p-2 text-[10px] font-bold rounded border ${
                          form.condition === c.v
                            ? "ring-2 ring-offset-1 ring-zinc-900" : "border-zinc-200"
                        }`}
                        style={{ background: c.color, color: "white" }}
                        data-testid={`pin-cond-${c.v}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-700">Notes</label>
            <Textarea value={form.notes} rows={2}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Bottom of drywall, behind cabinet" data-testid="pin-notes" />
          </div>
          <Button onClick={() => onSubmit(form)}
                  disabled={!form.value}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="pin-submit">
            Place Pin
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PinDetailSheet({ pin, onAddReading, onClose, scale }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    value: "", type: "moisture_content", condition: "elevated",
    notes: "", photo: null,
  });
  const cond = CONDITIONS.find(c => c.v === pin.current_condition) || CONDITIONS[0];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"
                     data-testid="fp-pin-detail">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crosshair className="w-5 h-5" style={{ color: cond.color }} />
            {pin.material_type} · {pin.current_value} {pin.readings[pin.readings.length-1]?.type === "wme" ? "WME" : "%"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Drying log and return-visit readings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex items-center gap-2 text-xs">
            <Badge style={{ background: cond.color }} className="text-white">
              {cond.label}
            </Badge>
            {pin.trend === "improving" && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border">
              <TrendingDown className="w-3 h-3 mr-1" /> Drying
            </Badge>}
            {pin.trend === "worsening" && <Badge className="bg-red-100 text-red-700 border-red-300 border">
              <TrendingUp className="w-3 h-3 mr-1" /> Wetting
            </Badge>}
            {pin.trend === "flat" && <Badge className="bg-zinc-100 text-zinc-700 border-zinc-300 border">
              <Minus className="w-3 h-3 mr-1" /> Flat
            </Badge>}
            {pin.stale_count >= 3 && <Badge className="bg-red-600 text-white">
              <AlertTriangle className="w-3 h-3 mr-1" /> Not drying ({pin.stale_count})
            </Badge>}
          </div>

          <div>
            <p className="text-xs font-semibold text-zinc-700 mb-1">Drying Log ({pin.readings.length})</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {pin.readings.map((r, i) => {
                const c = CONDITIONS.find(c => c.v === r.condition) || CONDITIONS[0];
                return (
                  <div key={r.id || i} className="flex items-center gap-2 text-[11px] border-b border-zinc-100 py-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                    <span className="font-mono font-bold">{r.value}</span>
                    <span className="text-zinc-500">{r.type === "wme" ? "WME" : r.type === "rh" ? "RH%" : "MC%"}</span>
                    <span className="text-zinc-400 ml-auto">{new Date(r.timestamp).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {!adding ? (
            <Button onClick={() => setAdding(true)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="pin-add-reading">
              <Plus className="w-4 h-4 mr-1" /> Add Return-Visit Reading
            </Button>
          ) : (
            <div className="space-y-2 bg-zinc-50 p-3 rounded border border-zinc-200">
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" value={form.value}
                       onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                       placeholder="New reading"
                       data-testid="pin-new-value" />
                <Select value={form.condition}
                        onValueChange={v => setForm(f => ({ ...f, condition: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map(c =>
                    <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea rows={2} value={form.notes}
                        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Notes for this reading" />
              <Button onClick={() => onAddReading(form)} disabled={!form.value}
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                      data-testid="pin-new-submit">
                Save Reading
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentSheet({ equipmentSheet, onSubmit, onClose }) {
  const [form, setForm] = useState({
    equipment_type: "Air Mover", asset_id: "", make_model: "",
    date_placed: new Date().toISOString().slice(0, 16),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="fp-eq-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wrench className="w-5 h-5 text-red-600" /> Place Equipment
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Pin a piece of drying equipment to the canvas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-zinc-700">Type</label>
            <Select value={form.equipment_type}
                    onValueChange={v => setForm(f => ({ ...f, equipment_type: v }))}>
              <SelectTrigger data-testid="eq-type"><SelectValue /></SelectTrigger>
              <SelectContent>{EQUIPMENT_TYPES.map(t =>
                <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-700">Asset / ID #</label>
            <Input value={form.asset_id} placeholder="DH-042"
                   onChange={e => setForm(f => ({ ...f, asset_id: e.target.value }))}
                   data-testid="eq-asset" />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-700">Make / Model</label>
            <Input value={form.make_model} placeholder="Dri-Eaz LGR 7000XLi"
                   onChange={e => setForm(f => ({ ...f, make_model: e.target.value }))}
                   data-testid="eq-model" />
          </div>
          <Button onClick={() => onSubmit(form)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="eq-submit">
            Place Equipment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AffectedAreaDialog({ onSubmit, onClose }) {
  const [damage, setDamage] = useState("water");
  const [label, setLabel] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="fp-aa-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Tag Affected Area</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Pick a damage type and label this zone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-3 gap-1.5">
            {DAMAGE_TYPES.map(d => (
              <button key={d.v} onClick={() => setDamage(d.v)}
                      className={`p-2 text-[10px] font-bold rounded border-2 text-white ${
                        damage === d.v ? "ring-2 ring-offset-1 ring-zinc-900" : "border-transparent"
                      }`}
                      style={{ background: d.color }}
                      data-testid={`aa-type-${d.v}`}>
                {d.label}
              </button>
            ))}
          </div>
          <Input value={label} onChange={e => setLabel(e.target.value)}
                 placeholder="e.g., Primary Water Intrusion Zone"
                 data-testid="aa-label" />
          <Button onClick={() => onSubmit(damage, label)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="aa-submit">
            Save Affected Area
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LabelDialog({ roomId, current, onSubmit, onClose }) {
  const [text, setText] = useState(current);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="fp-label-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Room Label</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Name this room.
          </DialogDescription>
        </DialogHeader>
        <Input value={text} onChange={e => setText(e.target.value)}
               placeholder="Master Bedroom"
               autoFocus
               data-testid="label-input" />
        <Button onClick={() => onSubmit(text)}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="label-submit">
          Save
        </Button>
      </DialogContent>
    </Dialog>
  );
}
